function normalizarDescripcionTipo(descripcion: string): string {
  return (descripcion ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}

/**
 * Configuración centralizada de colores para estados y tipos de visitas
 * 
 * Este archivo contiene todas las definiciones de colores utilizadas en:
 * - Tablas de visitas (badges)
 * - Selectores de filtros (círculos de color)
 * - Gráficos (Chart.js)
 * - Mapas (marcadores)
 * - Estado General de Planes (gráfico circular, etiquetas de la API)
 * 
 * ÚNICA FUENTE DE VERDAD para los colores de estados y tipos de visitas
 */

/**
 * Colores para estados de visitas
 */
export const COLORES_ESTADOS = {
  agendada: {
    hex: '#8EBDEF',
    rgb: '142, 189, 239',
    rgba: (opacity: number = 1) => `rgba(142, 189, 239, ${opacity})`,
    nombre: 'Azul'
  },
  planificada: {
    hex: '#B8A9D4',
    rgb: '184, 169, 212',
    rgba: (opacity: number = 1) => `rgba(184, 169, 212, ${opacity})`,
    nombre: 'Lavanda'
  },
  finalizada: {
    hex: '#D8EADD',
    rgb: '216, 234, 221',
    rgba: (opacity: number = 1) => `rgba(216, 234, 221, ${opacity})`,
    nombre: 'Verde claro'
  },
  cancelada: {
    hex: '#F1AA9B',
    rgb: '241, 170, 155',
    rgba: (opacity: number = 1) => `rgba(241, 170, 155, ${opacity})`,
    nombre: 'Coral'
  },
  reagendada: {
    hex: '#FAE9C6',
    rgb: '250, 233, 198',
    rgba: (opacity: number = 1) => `rgba(250, 233, 198, ${opacity})`,
    nombre: 'Ámbar claro'
  },
  default: {
    hex: '#E2E4E6',
    rgb: '226, 228, 230',
    rgba: (opacity: number = 1) => `rgba(226, 228, 230, ${opacity})`,
    nombre: 'Gris'
  }
} as const;

/**
 * Colores para tipos de visitas (ids 1–5 del catálogo obtenerTiposVisita).
 * Ids 1–4 conservan el mismo hex que el catálogo anterior; id 5 es nuevo.
 */
export const COLORES_TIPOS = {
  /** id 1 — Diagnóstico */
  diagnostico: {
    hex: '#14867C',
    rgb: '20, 134, 124',
    rgba: (opacity: number = 1) => `rgba(20, 134, 124, ${opacity})`,
    nombre: 'Verde azulado'
  },
  /** id 2 — Diseño plan de negocio (legacy: Diseño, Formulación del plan) */
  disenoPlanNegocio: {
    hex: '#AFDFB2',
    rgb: '175, 223, 178',
    rgba: (opacity: number = 1) => `rgba(175, 223, 178, ${opacity})`,
    nombre: 'Verde'
  },
  /** id 3 — Asesoría base (legacy: Implementación, Asesoría) */
  asesoriaBase: {
    hex: '#7CA6DD',
    rgb: '124, 166, 221',
    rgba: (opacity: number = 1) => `rgba(124, 166, 221, ${opacity})`,
    nombre: 'Celeste'
  },
  /** id 4 — Asesoría de especialistas individual (legacy: Bolsa de Visitas) */
  asesoriaEspecialistasIndividual: {
    hex: '#F5C880',
    rgb: '245, 200, 128',
    rgba: (opacity: number = 1) => `rgba(245, 200, 128, ${opacity})`,
    nombre: 'Ámbar'
  },
  /** id 5 — Encuesta margen bruto */
  encuestaMargenBruto: {
    hex: '#B33771',
    rgb: '179, 55, 113',
    rgba: (opacity: number = 1) => `rgba(179, 55, 113, ${opacity})`,
    nombre: 'Magenta'
  },
  /** Alias retrocompat (mismo hex que ids 2–4) */
  diseno: { hex: '#AFDFB2', rgb: '175, 223, 178', rgba: (o: number = 1) => `rgba(175, 223, 178, ${o})`, nombre: 'Verde' },
  implementacion: { hex: '#7CA6DD', rgb: '124, 166, 221', rgba: (o: number = 1) => `rgba(124, 166, 221, ${o})`, nombre: 'Celeste' },
  bolsaVisitas: { hex: '#F5C880', rgb: '245, 200, 128', rgba: (o: number = 1) => `rgba(245, 200, 128, ${o})`, nombre: 'Ámbar' },
  bolsaHoras: { hex: '#F5C880', rgb: '245, 200, 128', rgba: (o: number = 1) => `rgba(245, 200, 128, ${o})`, nombre: 'Ámbar' },
  formulacion: { hex: '#AFDFB2', rgb: '175, 223, 178', rgba: (o: number = 1) => `rgba(175, 223, 178, ${o})`, nombre: 'Verde' },
  asesoria: { hex: '#7CA6DD', rgb: '124, 166, 221', rgba: (o: number = 1) => `rgba(124, 166, 221, ${o})`, nombre: 'Celeste' },
  asesoriaExtra: { hex: '#F5C880', rgb: '245, 200, 128', rgba: (o: number = 1) => `rgba(245, 200, 128, ${o})`, nombre: 'Ámbar' },
  default: {
    hex: '#6b7280',
    rgb: '107, 114, 128',
    rgba: (opacity: number = 1) => `rgba(107, 114, 128, ${opacity})`,
    nombre: 'Gris'
  }
} as const;

/**
 * Mapeo de clases CSS a colores de estados
 */
export const MAPEO_CLASES_ESTADOS: { [key: string]: keyof typeof COLORES_ESTADOS } = {
  'estado-agendada': 'agendada',
  'estado-planificada': 'planificada',
  'estado-finalizada': 'finalizada',
  'estado-cancelada': 'cancelada',
  'estado-reagendada': 'reagendada',
  'estado-default': 'default'
};

/**
 * Mapeo de clases CSS a colores de tipos
 */
export const MAPEO_CLASES_TIPOS: { [key: string]: keyof typeof COLORES_TIPOS } = {
  'tipo-diagnostico': 'diagnostico',
  'tipo-diseno-plan-negocio': 'disenoPlanNegocio',
  'tipo-asesoria-base': 'asesoriaBase',
  'tipo-asesoria-especialistas-individual': 'asesoriaEspecialistasIndividual',
  'tipo-encuesta-margen-bruto': 'encuestaMargenBruto',
  // Retrocompat: clases legacy → mismo slot de color (ids 2–4)
  'tipo-diseno': 'disenoPlanNegocio',
  'tipo-formulacion': 'disenoPlanNegocio',
  'tipo-implementacion': 'asesoriaBase',
  'tipo-asesoria': 'asesoriaBase',
  'tipo-bolsa-visitas': 'asesoriaEspecialistasIndividual',
  'tipo-bolsa-horas': 'asesoriaEspecialistasIndividual',
  'tipo-asesoria-extra': 'asesoriaEspecialistasIndividual',
  'tipo-default': 'default'
};

/**
 * Obtiene el color hexadecimal para un estado basado en su clase CSS
 */
export function obtenerColorEstado(clase: string): string {
  const clave = MAPEO_CLASES_ESTADOS[clase] || 'default';
  return COLORES_ESTADOS[clave].hex;
}

/**
 * Obtiene el color hexadecimal para un tipo basado en su clase CSS
 */
export function obtenerColorTipo(clase: string): string {
  const clave = MAPEO_CLASES_TIPOS[clase] || 'default';
  return COLORES_TIPOS[clave].hex;
}

/**
 * Obtiene el color RGBA para un estado (opacidad 1 = color puro, sin transparencia)
 */
export function obtenerColorEstadoRGBA(clase: string, opacity: number = 1): string {
  const clave = MAPEO_CLASES_ESTADOS[clase] || 'default';
  return COLORES_ESTADOS[clave].rgba(opacity);
}

/**
 * Obtiene el color RGBA para un tipo (opacidad 1 = color puro, sin transparencia)
 */
export function obtenerColorTipoRGBA(clase: string, opacity: number = 1): string {
  const clave = MAPEO_CLASES_TIPOS[clase] || 'default';
  return COLORES_TIPOS[clave].rgba(opacity);
}

/**
 * Luminancia relativa WCAG (0–1). Misma fórmula que las etiquetas del gráfico Estado General de Planes.
 */
export function luminanciaRelativaHex(hex: string): number {
  const raw = (hex ?? '#6b7280').replace(/^#/, '');
  const full =
    raw.length === 3 ? raw.split('').map(ch => ch + ch).join('') : raw;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return 0.5;
  const r = (n >> 16) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const lin = [r, g, b].map(c =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * lin[0]! + 0.7152 * lin[1]! + 0.0722 * lin[2]!;
}

const UMBRAL_LUMINANCIA_TEXTO_PLAN = 0.45;

/**
 * Color de texto legible sobre el hex del segmento (gráfico circular y badges /gestion).
 */
export function colorTextoLegibleSobreHexEstadoPlan(hex: string): '#ffffff' | '#1a1a1a' {
  return luminanciaRelativaHex(hex) < UMBRAL_LUMINANCIA_TEXTO_PLAN ? '#ffffff' : '#1a1a1a';
}

/**
 * Colores para Estado General de Planes (gráfico estado de planes).
 * Única fuente de verdad para hex/rgb/rgba de Diagnóstico, Diseño y Ejecución (también badges en /gestion).
 * Etiquetas devueltas por la API: Diagnóstico, Diseño, Ejecución (legacy: Implementación).
 */
export const COLORES_ESTADO_PLANES = {
  'Diagnóstico': {
    hex: '#14867C',
    rgb: '20, 134, 124',
    rgba: (opacity: number = 1) => `rgba(20, 134, 124, ${opacity})`,
    nombre: 'Verde azulado'
  },
  'Diseño': {
    hex: '#AFDFB2',
    rgb: '175, 223, 178',
    rgba: (opacity: number = 1) => `rgba(175, 223, 178, ${opacity})`,
    nombre: 'Verde claro'
  },
  'Ejecución': {
    hex: '#7CA6DD',
    rgb: '124, 166, 221',
    rgba: (opacity: number = 1) => `rgba(124, 166, 221, ${opacity})`,
    nombre: 'Celeste'
  },
  'Cerrado': {
    hex: '#6b7280',
    rgb: '107, 114, 128',
    rgba: (opacity: number = 1) => `rgba(107, 114, 128, ${opacity})`,
    nombre: 'Gris'
  },
  default: {
    hex: '#6b7280',
    rgb: '107, 114, 128',
    rgba: (opacity: number = 1) => `rgba(107, 114, 128, ${opacity})`,
    nombre: 'Gris'
  }
} as const;

/** Etiquetas conocidas de Estado General de Planes (para type-safety en mapeos) */
export type LabelEstadoPlan = keyof Omit<typeof COLORES_ESTADO_PLANES, 'default'>;

/** Descripción normalizada → etiqueta canónica del gráfico estadoPlanes (actual + legacy). */
export const ETIQUETA_CANONICA_ESTADO_PLAN_POR_DESCRIPCION: Record<string, LabelEstadoPlan> = {
  diagnostico: 'Diagnóstico',
  diseno: 'Diseño',
  'diseno plan de negocio': 'Diseño',
  'formulacion del plan': 'Diseño',
  ejecucion: 'Ejecución',
  implementacion: 'Ejecución',
  'asesoria tecnica individual': 'Ejecución',
  cerrado: 'Cerrado'
};

/**
 * Resuelve etiqueta canónica (Diagnóstico, Diseño, Ejecución) desde texto o id de API.
 */
export function resolverEtiquetaCanonicaEstadoPlan(
  raw: string | number | null | undefined
): LabelEstadoPlan | null {
  if (raw === null || raw === undefined) return 'Diagnóstico';
  if (typeof raw === 'number') {
    if (raw === 1) return 'Diagnóstico';
    if (raw === 2) return 'Diseño';
    if (raw === 3) return 'Ejecución';
    if (raw === 4) return 'Cerrado';
    return null;
  }
  const t = String(raw).trim();
  if (t === '' || t.toLowerCase() === 'sin plan') return 'Diagnóstico';
  if (t === 'Implementación') return 'Ejecución';
  if (t in COLORES_ESTADO_PLANES) return t as LabelEstadoPlan;
  if (/^\d+$/.test(t)) {
    return resolverEtiquetaCanonicaEstadoPlan(Number(t));
  }
  return ETIQUETA_CANONICA_ESTADO_PLAN_POR_DESCRIPCION[normalizarDescripcionTipo(t)] ?? null;
}

/**
 * Obtiene el color (background/border para Chart.js) para una etiqueta de Estado General de Planes.
 * Vacío, null coercido o "Sin plan" se tratan como Diagnóstico (alineado con lista /gestion).
 */
export function obtenerColorEstadoPlan(
  label: string
): { background: string; border: string } {
  const canon = resolverEtiquetaCanonicaEstadoPlan(label);
  const entrada = canon
    ? COLORES_ESTADO_PLANES[canon]
    : COLORES_ESTADO_PLANES.default;
  return {
    background: entrada.hex,
    border: entrada.hex
  };
}

/**
 * Colores para Avance de Visita (gráfico torta en /inicio).
 * Etiquetas devueltas por la API: Obligatorias, Bolsa de Visitas.
 */
export const COLORES_AVANCE_VISITAS = {
  'Obligatorias': {
    hex: '#14867C',
    rgb: '20, 134, 124',
    rgba: (opacity: number = 1) => `rgba(20, 134, 124, ${opacity})`,
    nombre: 'Verde azulado'
  },
  'Bolsa de Visitas': {
    hex: '#F5C880',
    rgb: '245, 200, 128',
    rgba: (opacity: number = 1) => `rgba(245, 200, 128, ${opacity})`,
    nombre: 'Ámbar'
  },
  default: {
    hex: '#6b7280',
    rgb: '107, 114, 128',
    rgba: (opacity: number = 1) => `rgba(107, 114, 128, ${opacity})`,
    nombre: 'Gris'
  }
} as const;

export type LabelAvanceVisita = keyof Omit<typeof COLORES_AVANCE_VISITAS, 'default'>;

/** Orden fijo de leyenda en el gráfico Avance de Visita del dashboard. */
export const SERIES_AVANCE_VISITAS_DASHBOARD: { label: LabelAvanceVisita; color: string }[] = [
  { label: 'Obligatorias', color: COLORES_AVANCE_VISITAS['Obligatorias'].hex },
  { label: 'Bolsa de Visitas', color: COLORES_AVANCE_VISITAS['Bolsa de Visitas'].hex }
];

export interface AvanceVisitaItem {
  label: string;
  value: number;
}

/**
 * Resuelve etiqueta canónica para Avance de Visita desde texto de API.
 */
export function resolverEtiquetaCanonicaAvanceVisita(
  raw: string | null | undefined
): LabelAvanceVisita | null {
  if (raw === null || raw === undefined) return null;
  const t = String(raw).trim();
  if (t in COLORES_AVANCE_VISITAS) return t as LabelAvanceVisita;
  const norm = normalizarDescripcionTipo(t);
  if (norm === 'obligatorias') return 'Obligatorias';
  if (norm === 'bolsa de visitas' || norm === 'bolsa visitas') return 'Bolsa de Visitas';
  return null;
}

/**
 * Obtiene el color (background/border para Chart.js) para Avance de Visita.
 */
export function obtenerColorAvanceVisitas(
  label: string
): { background: string; border: string } {
  const canon = resolverEtiquetaCanonicaAvanceVisita(label);
  const entrada = canon
    ? COLORES_AVANCE_VISITAS[canon]
    : COLORES_AVANCE_VISITAS.default;
  return {
    background: entrada.hex,
    border: entrada.hex
  };
}

/**
 * Normaliza la respuesta de avanceVisitasCompletadas a items ordenados para el gráfico.
 * Devuelve [] si el total es 0 (estado vacío en UI).
 */
export function normalizarAvanceVisitasCompletadasValor(valor: unknown): AvanceVisitaItem[] {
  const acum: Record<LabelAvanceVisita, number> = {
    'Obligatorias': 0,
    'Bolsa de Visitas': 0
  };

  if (Array.isArray(valor)) {
    for (const item of valor) {
      if (!item || typeof item !== 'object') continue;
      const row = item as Record<string, unknown>;
      const etiqueta = String(row['label'] ?? row['etiqueta'] ?? '');
      const cantidad = row['value'] ?? row['valor'] ?? row['cantidad'];
      const canon = resolverEtiquetaCanonicaAvanceVisita(etiqueta);
      if (canon) {
        acum[canon] += Number(cantidad) || 0;
      }
    }
  }

  const items = SERIES_AVANCE_VISITAS_DASHBOARD.map(serie => ({
    label: serie.label,
    value: acum[serie.label] ?? 0
  }));

  const total = items.reduce((suma, item) => suma + item.value, 0);
  return total > 0 ? items : [];
}

/**
 * Colores para Reportes (Género, Etnia, etc.)
 */
export const COLORES_REPORTES: { [key: string]: { hex: string } } = {
  // Género
  'Femenino': { hex: '#7CA6DD' },
  'Masculino': { hex: '#3678BA' },
  'No se informa': { hex: '#E2E4E6' },
  // Etnia
  'Mapuche': { hex: '#AFDFB2' },
  'Atacameño': { hex: '#3678BA' },
  'No Pertenece': { hex: '#F5C880' },
  // Estado Plan de Negocio (consistencia con COLORES_ESTADO_PLANES)
  'Diagnóstico': { hex: '#14867C' },
  'Diseño': { hex: '#AFDFB2' },
  'Ejecución': { hex: '#7CA6DD' },
  'Implementación': { hex: '#7CA6DD' },
  'default': { hex: '#6b7280' }
};

/**
 * Caché para mantener los colores asignados en la sesión y evitar saltos
 */
const COLORES_ASIGNADOS_DINAMICAMENTE: { [key: string]: string } = {};

/**
 * Paleta de colores armoniosa para asignación automática 
 */
const PALETA_DINAMICA = [
  '#8EBDEF', '#D8EADD', '#FAE9C6', '#9C88FF', '#F1AA9B',
  '#00A8FF', '#4CD137', '#E1B12C', '#E84118', '#7F8FA6', 
  '#273C75', '#44BD32', '#0097E6', '#C23616', '#718093',
  '#8c7ae6', '#B33771', '#3B3B98', '#FD7272', '#2C3A47'
];
let paletaIndex = 0;

/**
 * Obtiene el color para una etiqueta de reporte.
 * Si la categoría no está predefinida, automáticamente se le asigna un color
 * vibrante permanente para la sesión del visor.
 */
export function obtenerColorReporte(label: string): string {
  // 1. Verificar si está estrictamente predefinido
  if (COLORES_REPORTES[label]) {
    return COLORES_REPORTES[label].hex;
  }
  
  // 2. Verificar si ya le hemos asignado un color en el ciclo de vida actual
  if (COLORES_ASIGNADOS_DINAMICAMENTE[label]) {
    return COLORES_ASIGNADOS_DINAMICAMENTE[label];
  }
  
  // 3. Asignar nuevo color automáticamente y almacenarlo
  const colorNuevo = PALETA_DINAMICA[paletaIndex % PALETA_DINAMICA.length];
  COLORES_ASIGNADOS_DINAMICAMENTE[label] = colorNuevo;
  paletaIndex++;
  
  return colorNuevo;
}
